import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { Input } from "@/shared/ui/components/ui/input";
import { Label } from "@/shared/ui/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { Eye, EyeOff } from "lucide-react";

export interface InputFieldBaseProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode;
  hint?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onErrorClear?: () => void;
}

const InputFieldBase = React.forwardRef<HTMLInputElement, InputFieldBaseProps>(
  (
    {
      className,
      type,
      label,
      hint,
      error,
      leftIcon,
      rightIcon,
      disabled,
      id,
      onChange,
      onErrorClear,
      value,
      ...props
    },
    ref,
  ) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);
    const isPassword = type === "password";
    const [showPassword, setShowPassword] = React.useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hasError && onErrorClear) {
        onErrorClear();
      }
      onChange?.(e);
    };

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const inputType = isPassword && showPassword ? "text" : type;
    const displayRightIcon =
      rightIcon || (isPassword ? (showPassword ? EyeOff : Eye) : null);

    return (
      <div className="w-full gap-3">
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "mb-3 block text-sm font-medium text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700",
            )}
          >
            {label}
          </Label>
        )}

        <div className="relative gap-2 group">
          {leftIcon && (
            <div
              className={cn(
                "absolute start-5 top-1/2 -translate-y-1/2 transition-colors text-grey-200",
                "group-focus-within:text-grey-900",
                disabled && "text-grey-200",
                hasError && "text-error-700 group-focus-within:text-error-700",
              )}
            >
              {leftIcon}
            </div>
          )}

          <Input
            id={inputId}
            ref={ref}
            type={inputType}
            disabled={disabled}
            onChange={handleChange}
            value={value}
            className={cn(
              " py-4 border-grey-100 bg-white text-sm font-normal shadow-none",
              "focus-visible:ring-0",
              "disabled:opacity-100",
              "text-grey-200",
              leftIcon && "ps-12",
              displayRightIcon && "pe-12",
              "hover:border-brand-green hover:text-grey-200",
              "focus-visible:border-brand-green focus-visible:text-grey-900",
              // Hide number input arrows
              type === "number" && [
                "[appearance:textfield]",
                "[&::-webkit-outer-spin-button]:appearance-none",
                "[&::-webkit-inner-spin-button]:appearance-none",
              ],
              disabled &&
              "bg-grey-50 border-grey-100 text-grey-200 cursor-not-allowed hover:border-grey-100 hover:text-grey-200",
              hasError &&
              "bg-error-bg border-error-700 text-error-700 placeholder:text-error-700 hover:border-error-700 focus-visible:border-error-700 focus-visible:text-error-700",
              className,
            )}
            {...props}
          />

          {displayRightIcon && (
            <button
              type="button"
              onClick={isPassword ? togglePasswordVisibility : undefined}
              disabled={disabled}
              className={cn(
                "absolute end-5 top-1/2 -translate-y-1/2 transition-colors text-grey-200",
                "group-focus-within:text-grey-900",
                disabled && "text-grey-200 cursor-not-allowed",
                hasError && "text-error-700 group-focus-within:text-error-700",
                isPassword && "cursor-pointer hover:opacity-80",
              )}
            >
              {isPassword ? (
                showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )
              ) : (
                rightIcon
              )}
            </button>
          )}
        </div>

        {(hint || error) && (
          <p
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              hasError && "text-error-700",
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  },
);

InputFieldBase.displayName = "InputFieldBase";

export interface InputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<InputFieldBaseProps, "name" | "value" | "onChange" | "error"> {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  error?: string;
}

function InputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: InputFieldProps<TFieldValues, TName> & {
    ref?: React.Ref<HTMLInputElement>;
  },
) {
  const { name, control, rules, error: errorProp, ...restProps } = props;

  if (control && name) {
    return (
      <Controller<TFieldValues, TName>
        name={name}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <InputFieldBase
            {...restProps}
            {...field}
            value={field.value ?? ""}
            error={fieldState.error?.message || errorProp}
          />
        )}
      />
    );
  }

  return <InputFieldBase {...restProps} error={errorProp} />;
}

const InputFieldWithRef = React.forwardRef(function InputFieldInner<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: InputFieldProps<TFieldValues, TName>,
  ref: React.Ref<HTMLInputElement>,
) {
  const { name, control, rules, error: errorProp, ...restProps } = props;

  if (control && name) {
    return (
      <Controller<TFieldValues, TName>
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <InputFieldBase
            {...restProps}
            {...field}
            value={field.value ?? ""}
            error={fieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  return <InputFieldBase {...restProps} error={errorProp} ref={ref} />;
}) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: InputFieldProps<TFieldValues, TName> & {
    ref?: React.Ref<HTMLInputElement>;
  },
) => React.ReactElement;

export { InputFieldWithRef as InputField };
